import { useState, useCallback } from 'react';
import axios from 'axios';

const useArchiveData = ({ getAuthHeaders, showSuccessMessage, setError }) => {
  const API_BACKEND = process.env.REACT_APP_SHOPKEEP_BACKEND || 'http://localhost:3001';
  const ARCHIVE_URL = `${API_BACKEND}/api/inventory/archive`;

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [archivedItems, setArchivedItems] = useState([]);
  const [archivedItemsCount, setArchivedItemsCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [quota, setQuota] = useState(null);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingQuota, setIsLoadingQuota] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const fetchPeriods = useCallback(async () => {
    setIsLoadingPeriods(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(`${ARCHIVE_URL}/periods`, config);
      if (response.data.success) {
        setPeriods(response.data.data.periods || []);
      }
    } catch (error) {
      console.error('Error fetching archive periods:', error);
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        setError(error.response?.data?.error || 'Failed to fetch archive periods');
      }
    } finally {
      setIsLoadingPeriods(false);
    }
  }, [getAuthHeaders, ARCHIVE_URL, setError]);

  const fetchItems = useCallback(async (year, month, params = {}) => {
    setIsLoadingItems(true);
    try {
      const config = await getAuthHeaders();
      const searchParams = new URLSearchParams();
      searchParams.set('limit', params.limit || 50);
      searchParams.set('offset', params.offset || 0);
      if (params.search) searchParams.set('search', params.search);
      searchParams.set('sortBy', params.sortBy || 'dateArchived');
      searchParams.set('sortOrder', params.sortOrder || 'desc');

      const response = await axios.get(
        `${ARCHIVE_URL}/${year}/${month}?${searchParams.toString()}`,
        config
      );
      if (response.data.success) {
        const data = response.data.data;
        setArchivedItems(data.items || []);
        setArchivedItemsCount(data.totalCount ?? data.count ?? 0);
        setHasMore(data.hasMore ?? false);
      }
    } catch (error) {
      console.error('Error fetching archived items:', error);
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        setError(error.response?.data?.error || 'Failed to fetch archived items');
      }
    } finally {
      setIsLoadingItems(false);
    }
  }, [getAuthHeaders, ARCHIVE_URL, setError]);

  const restoreItems = useCallback(async (year, month, itemIds) => {
    setIsRestoring(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${ARCHIVE_URL}/restore`,
        { year, month, itemIds },
        config
      );
      if (response.data.success) {
        setArchivedItems(prev => prev.filter(item => !itemIds.includes(item.id)));
        setArchivedItemsCount(prev => prev - response.data.data.restoredCount);
        showSuccessMessage(`${response.data.data.restoredCount} item(s) restored to active inventory`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error restoring items:', error);
      const errorMsg = error.response?.status === 429
        ? error.response?.data?.error || 'Insufficient inventory quota to restore items'
        : error.response?.data?.error || 'Failed to restore items';
      setError(errorMsg);
      return false;
    } finally {
      setIsRestoring(false);
    }
  }, [getAuthHeaders, ARCHIVE_URL, showSuccessMessage, setError]);

  const fetchQuota = useCallback(async () => {
    setIsLoadingQuota(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(`${ARCHIVE_URL}/quota`, config);
      if (response.data.success) {
        setQuota(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching archive quota:', error);
    } finally {
      setIsLoadingQuota(false);
    }
  }, [getAuthHeaders, ARCHIVE_URL]);

  return {
    periods,
    selectedPeriod,
    setSelectedPeriod,
    archivedItems,
    archivedItemsCount,
    hasMore,
    quota,
    isLoadingPeriods,
    isLoadingItems,
    isLoadingQuota,
    isRestoring,
    fetchPeriods,
    fetchItems,
    restoreItems,
    fetchQuota,
  };
};

export default useArchiveData;
